import { DateTime } from 'luxon';

/**
 * 📅 ENHANCED DATE HELPERS LIBRARY - Task 3
 * 
 * Librărie centralizată de utilități pentru date/timp cu Luxon support.
 * Organizează funcțiile helper separate de store pentru clean architecture.
 * 
 * 🎯 PRINCIPII:
 * - Backward compatibility completă cu globalDateStore exports
 * - Pragmatic over perfect - focus pe funcționalitate utilă
 * - Timezone-aware cu Luxon pentru conversii robuste
 * - Testing utilities integrate pentru development workflow
 */

// =============================================================================
// DATABASE & STORAGE HELPERS
// =============================================================================

/**
 * Obține data curentă în format UTC ISO pentru storage în bază de date
 * Garantează consistența datelor indiferent de timezone-ul utilizatorului
 * 
 * @returns {string} Data în format ISO UTC (ex: "2025-01-15T10:30:45.123Z")
 * @example
 * getCurrentDateForDB() // "2025-01-15T10:30:45.123Z"
 */
export function getCurrentDateForDB(): string {
  // Folosim Date.now() direct pentru a evita dependența circulară
  return new Date().toISOString();
}

/**
 * Obține data curentă în format YYYY-MM-DD pentru UI și form fields
 * Folosește timezone-ul local/preferat al utilizatorului
 * 
 * @returns {string} Data în format YYYY-MM-DD (ex: "2025-01-15") 
 * @example
 * getCurrentDateYMD() // "2025-01-15"
 */
export function getCurrentDateYMD(): string {
  // Folosim DateTime.now() direct pentru a evita dependența circulară
  return DateTime.now().toFormat('yyyy-MM-dd');
}

// =============================================================================
// ROMANIAN LOCALIZATION HELPERS
// =============================================================================

/**
 * Numele lunilor în română pentru afișare user-friendly
 */
export const ROMANIAN_MONTHS = [
  'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
] as const;

/**
 * Numele lunilor în română - versiuni scurte
 */
export const ROMANIAN_MONTHS_SHORT = [
  'Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun',
  'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
] as const;

/**
 * Convertește un input month HTML5 (YYYY-MM) în text românesc
 * @param monthValue Valoarea din input month (ex: "2025-01")
 * @returns Text în română (ex: "Ianuarie 2025")
 * @example
 * formatMonthToRomanian("2025-01") // "Ianuarie 2025"
 * formatMonthToRomanian("2024-12") // "Decembrie 2024"
 */
export function formatMonthToRomanian(monthValue: string): string {
  if (!monthValue || !monthValue.includes('-')) {
    return monthValue;
  }
  
  const [year, month] = monthValue.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  
  if (monthIndex < 0 || monthIndex >= 12) {
    return monthValue;
  }
  
  return `${ROMANIAN_MONTHS[monthIndex]} ${year}`;
}

/**
 * Convertește un input month HTML5 (YYYY-MM) în text românesc scurt
 * @param monthValue Valoarea din input month (ex: "2025-01")
 * @returns Text în română scurt (ex: "Ian 2025")
 */
export function formatMonthToRomanianShort(monthValue: string): string {
  if (!monthValue || !monthValue.includes('-')) {
    return monthValue;
  }
  
  const [year, month] = monthValue.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  
  if (monthIndex < 0 || monthIndex >= 12) {
    return monthValue;
  }
  
  return `${ROMANIAN_MONTHS_SHORT[monthIndex]} ${year}`;
}

/**
 * Obține luna curentă în format YYYY-MM pentru input month
 * @returns String în format YYYY-MM (ex: "2025-01")
 */
export function getCurrentMonthValue(): string {
  // Folosim DateTime.now() direct pentru a evita dependența circulară
  return DateTime.now().toFormat('yyyy-MM');
}

// =============================================================================
// CALENDAR UTILITIES - Pentru calendarul custom
// =============================================================================

/**
 * Numele zilelor săptămânii în română (forma scurtă)
 * Folosim Me pentru Miercuri pentru a evita conflictul cu M (Marți)
 */
export const ROMANIAN_WEEKDAYS_SHORT = ['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D'] as const;

/**
 * Numele zilelor săptămânii în română (forma lungă)
 */
export const ROMANIAN_WEEKDAYS = [
  'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'
] as const;

/**
 * Generează zilele unei luni pentru calendar
 * @param year Anul (ex: 2025)
 * @param month Luna (0-11, ianuarie = 0)
 * @returns Array cu informații despre fiecare zi din calendar
 */
export function generateCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const today = new Date();
  
  // Primul zi al săptămânii (0 = duminică, 1 = luni, etc.)
  // Ajustăm pentru că în România săptămâna începe cu luni
  const firstDayOfWeek = (firstDay.getDay() + 6) % 7; // Convertim la format luni=0
  
  const days = [];
  
  // Zilele din luna precedentă pentru completarea începutului
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
  
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    days.push({
      day: prevMonthLastDay - i,
      month: prevMonth,
      year: prevYear,
      isCurrentMonth: false,
      isToday: false,
      date: new Date(prevYear, prevMonth, prevMonthLastDay - i)
    });
  }
  
  // Zilele din luna curentă
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    const isToday = date.toDateString() === today.toDateString();
    
    days.push({
      day,
      month,
      year,
      isCurrentMonth: true,
      isToday,
      date
    });
  }
  
  // Zilele din luna următoare pentru completarea sfârșitului
  const remainingDays = 42 - days.length; // Calendar 6x7 = 42 celule
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  
  for (let day = 1; day <= remainingDays; day++) {
    days.push({
      day,
      month: nextMonth,
      year: nextYear,
      isCurrentMonth: false,
      isToday: false,
      date: new Date(nextYear, nextMonth, day)
    });
  }
  
  return days;
}

/**
 * Convertește o dată la formatul YYYY-MM pentru month input
 * @param date Obiect Date
 * @returns String în format YYYY-MM
 */
export function dateToMonthValue(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Convertește un month value (YYYY-MM) la obiect Date
 * @param monthValue String în format YYYY-MM
 * @returns Obiect Date cu prima zi a lunii
 */
export function monthValueToDate(monthValue: string): Date {
  const [year, month] = monthValue.split('-').map(Number);
  return new Date(year, month - 1, 1);
}

/**
 * Verifică dacă două date sunt în aceeași lună
 * @param date1 Prima dată
 * @param date2 A doua dată
 * @returns Boolean
 */
export function isSameMonth(date1: Date, date2: Date): boolean {
  return date1.getFullYear() === date2.getFullYear() && 
         date1.getMonth() === date2.getMonth();
}

/**
 * Obține prima zi a unei luni
 * @param year Anul
 * @param month Luna (0-11)
 * @returns Data primei zile
 */
export function getFirstDayOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1);
}

/**
 * Obține ultima zi a unei luni
 * @param year Anul
 * @param month Luna (0-11)
 * @returns Data ultimei zile
 */
export function getLastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0);
}

/**
 * Convertește orice dată în format database-ready UTC ISO
 * Utilă pentru normalizarea datelor înainte de storage
 * 
 * @param {Date | string} date Data de convertit
 * @returns {string} Data în format ISO UTC
 * @example
 * toDBFormat(new Date()) // "2025-01-15T10:30:45.123Z"
 * toDBFormat("2025-01-15") // "2025-01-15T00:00:00.000Z"
 */
export function toDBFormat(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString();
}

// =============================================================================
// DISPLAY & FORMATTING HELPERS  
// =============================================================================

/**
 * Formatează data pentru afișare user-friendly în timezone-ul preferat
 * 
 * @param {Date | string} date Data de formatat
 * @param {string} format Format Luxon (ex: 'dd/MM/yyyy', 'dd LLLL yyyy')
 * @returns {string} Data formatată conform timezone-ului utilizatorului
 * @example
 * formatDateForDisplay(new Date(), 'dd/MM/yyyy') // "15/01/2025"
 * formatDateForDisplay("2025-01-15", 'dd LLLL yyyy') // "15 ianuarie 2025"
 */
export function formatDateForDisplay(
  date: Date | string, 
  format: string = 'dd LLLL yyyy'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return DateTime.fromJSDate(dateObj)
    .setLocale('ro') // Explicit Romanian locale pentru consistență
    .toFormat(format);
}

/**
 * Formatează data pentru header-uri și componente rapide
 * Folosește formatul standard românesc
 * 
 * @param {Date | string} date Data de formatat
 * @returns {string} Data în format "15 Ianuarie 2025"
 * @example
 * formatDateHeader(new Date()) // "15 Ianuarie 2025"
 */
export function formatDateHeader(date: Date | string): string {
  return formatDateForDisplay(date, 'dd LLLL yyyy');
}

// =============================================================================
// DEBUGGING & LOGGING HELPERS
// =============================================================================

/**
 * Adaugă timestamp consistent la mesajele de logging
 * Folosește timezone-ul utilizatorului pentru debugging local
 * 
 * @param {string} message Mesajul de logat
 * @param {'log' | 'warn' | 'error'} level Nivelul de logging (default: 'log')
 * @example
 * logWithTimestamp("User logged in") // "[15:30:45] User logged in"
 * logWithTimestamp("API error", "error") // "[15:30:45] API error"
 */
export function logWithTimestamp(
  message: string, 
  level: 'log' | 'warn' | 'error' = 'log'
): void {
  const timestamp = DateTime.now().toFormat('HH:mm:ss');
  console[level](`[${timestamp}] ${message}`);
}

/**
 * Creează string de debugging cu informații complete de context
 * Utilă pentru troubleshooting probleme de timezone și date
 * 
 * @param {string} context Contextul operațiunii (ex: "User Registration")
 * @returns {string} String complet cu timezone info pentru debugging
 * @example
 * createDebugTimestamp("Transaction Save") 
 * // "Transaction Save - 2025-01-15T15:30:45.123Z (UTC) | 2025-01-15T18:30:45.123+02:00 (Europe/Bucharest)"
 */
export function createDebugTimestamp(context: string): string {
  const now = new Date();
  const utcISO = now.toISOString();
  const localISO = DateTime.now().toISO();
  
  return `${context} - ${utcISO} (UTC) | ${localISO} (Local)`;
}

// =============================================================================
// CACHE & PERFORMANCE HELPERS
// =============================================================================

/**
 * Generează timestamp pentru cache-uri cu eficiență optimizată
 * Rotunjeste la 5 minute pentru a reduce cache misses și îmbunătăți hit rate
 * 
 * @returns {number} Timestamp rotunjit la nearest 5 minute pentru cache
 * @example
 * getQueryCacheTimestamp() // 1705321500000 (rotunjit la 5 min)
 */
export function getQueryCacheTimestamp(): number {
  const now = Date.now();
  const roundToMinutes = 5;
  const msInMinute = 60 * 1000;
  const roundTo = roundToMinutes * msInMinute;
  
  return Math.floor(now / roundTo) * roundTo;
}

/**
 * Creează cache key cu expirare automată pentru queries
 * Combină query params cu timestamp rotunjit pentru efficiency
 * 
 * @param {string} queryType Tipul de query (ex: "transactions", "balance")
 * @param {Record<string, any>} params Parametrii query pentru unicitate
 * @returns {string} Cache key cu timestamp pentru expirare automată
 * @example
 * createCacheKey("transactions", { userId: 123, month: "2025-01" })
 * // "transactions_userId:123_month:2025-01_t:1705321500000"
 */
export function createCacheKey(
  queryType: string, 
  params: Record<string, string | number | boolean> = {}
): string {
  const timestamp = getQueryCacheTimestamp();
  const paramString = Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join('_');
    
  return `${queryType}_${paramString}_t:${timestamp}`;
}

// =============================================================================
// TIMEZONE UTILITIES
// =============================================================================

/**
 * Convertește dată în timezone-ul utilizatorului folosind Luxon
 * 
 * @param {Date} date Data de convertit
 * @param {string} [timezone] Timezone țintă (default: din store)
 * @returns {DateTime} DateTime object în timezone-ul specificat
 * @example
 * toUserTimezone(new Date()) // DateTime în timezone-ul utilizatorului
 * toUserTimezone(new Date(), "America/New_York") // DateTime în Eastern Time
 */
export function toUserTimezone(date: Date, timezone?: string): DateTime {
  const targetTimezone = timezone || 'local';
  return DateTime.fromJSDate(date).setZone(targetTimezone);
}

/**
 * Convertește dată din timezone specificat către UTC
 * 
 * @param {Date} date Data de convertit  
 * @param {string} [sourceTimezone] Timezone sursă (default: din store)
 * @returns {DateTime} DateTime object în UTC
 * @example
 * toUTC(new Date()) // DateTime în UTC din timezone-ul utilizatorului
 * toUTC(new Date(), "America/New_York") // DateTime în UTC din Eastern Time
 */
export function toUTC(date: Date, sourceTimezone?: string): DateTime {
  const fromTimezone = sourceTimezone || 'local';
  return DateTime.fromJSDate(date, { zone: fromTimezone }).toUTC();
}

// =============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// =============================================================================

/**
 * Obține timestamp-ul curent în milisecunde (BACKWARD COMPATIBILITY)
 * @returns {number} Timestamp în ms
 */
export const getCurrentTimestamp = (): number => Date.now();

/**
 * Obține data curentă în format ISO (BACKWARD COMPATIBILITY)
 * @returns {string} Data în format ISO
 */
export const getCurrentDateISO = (): string => new Date().toISOString();

// =============================================================================
// TESTING UTILITIES (pentru development workflow)
// =============================================================================

/**
 * Helper pentru testing - permite controlul timpului în teste
 * DEPRECATED: Use mock time infrastructure from test/mockTime.ts instead
 * 
 * @param {Date} testDate Data de setat pentru teste
 * @example
 * setTestTime(new Date('2025-01-15T10:00:00Z')) // Pentru teste
 */
export function setTestTime(testDate: Date): void {
  console.warn('setTestTime is deprecated. Use mock time infrastructure from test/mockTime.ts');
}

/**
 * Helper pentru testing - avansează timpul cu X milisecunde  
 * DEPRECATED: Use mock time infrastructure from test/mockTime.ts instead
 * 
 * @param {number} milliseconds Milisecunde de avansat
 * @example
 * advanceTestTime(5 * 60 * 1000) // Avansează cu 5 minute
 */
export function advanceTestTime(milliseconds: number): void {
  console.warn('advanceTestTime is deprecated. Use mock time infrastructure from test/mockTime.ts');
} 